import {
  keepPreviousData,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  AdditionalColumnsListDto,
  AllowedDomainListDto,
  BillingInvoiceContactCompany,
  OrganizationDto,
  OrganizationOverviewDto,
  OrganizationUsersGroupedDto,
  YourTeamDto,
  OrganizationListDto,
} from '@sib/api';
import { QueryKey } from '@sib/api/enums';
import { useUserSetupStore } from '@sib/auth';
import { CACHE_TIME } from '../constants';
import { SpendBrainTableName } from './preferences';

export const useGetOrganizationOverview = (
  organizationId?: string,
  options?: UseQueryOptions<OrganizationOverviewDto>
): UseQueryResult<OrganizationOverviewDto> =>
  useQuery({
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.OrganizationOverview,
      organizationId,
      organizationId,
    ],
    queryFn: async () =>
      api.organization.organizationControllerGetOrganizationOverview(
        organizationId ?? ''
      ),
    ...options,
  });

export const useGetOrganization = (
  organizationId?: string,
  options?: UseQueryOptions<OrganizationDto>
): UseQueryResult<OrganizationDto> => {
  const { setOrganization, setActionPermissions } = useUserSetupStore();

  return useQuery({
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.Organization, organizationId],
    queryFn: async () => {
      const data =
        await api.organization.organizationControllerGetOrganizationById(
          organizationId || ''
        );
      setOrganization({
        name: data.name,
      });
      setActionPermissions(data.permissions || {});

      return data;
    },
    ...options,
  });
};

export const useGetOrganizationUsers = (
  organizationId?: string,
  options?: UseQueryOptions<OrganizationUsersGroupedDto>
): UseQueryResult<OrganizationUsersGroupedDto> =>
  useQuery({
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.OrganizationUsers, organizationId],
    queryFn: async () =>
      await api.organization.userManagementControllerGetOrganizationUsers(
        organizationId || ''
      ),
    ...options,
  });

interface UseGetYourTeamPayload {
  organizationId?: string;
  isVectorOrganization: boolean | null;
}

export const useGetYourTeam = (
  { organizationId, isVectorOrganization }: UseGetYourTeamPayload,
  options?: UseQueryOptions<YourTeamDto>
): UseQueryResult<YourTeamDto> =>
  useQuery({
    enabled: !!organizationId && typeof isVectorOrganization === 'boolean',
    queryKey: [QueryKey.Resource.OrganizationTeamMembers, organizationId],
    queryFn: async () => {
      const data = await api.organization.organizationControllerGetYourTeam(
        organizationId || '',
        {
          billingContactProvider: isVectorOrganization
            ? BillingInvoiceContactCompany.Vector
            : BillingInvoiceContactCompany.Sib,
        }
      );

      return data;
    },
    ...options,
  });

export const useGetAllowedDomains = (
  organizationId?: string,
  options?: UseQueryOptions<AllowedDomainListDto>
): UseQueryResult<AllowedDomainListDto> =>
  useQuery({
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.OrganizationAllowedDomains, organizationId],
    queryFn: async () =>
      api.organization.organizationControllerFindAllowedDomains(
        organizationId || ''
      ),
    ...options,
  });

interface UseGetAdditionalColumnsPayload {
  organizationId?: string;
  tableName: SpendBrainTableName;
  options?: UseQueryOptions<AdditionalColumnsListDto>;
}

export const useGetAdditionalColumns = ({
  organizationId,
  tableName,
  options,
}: UseGetAdditionalColumnsPayload): UseQueryResult<AdditionalColumnsListDto> =>
  useQuery({
    enabled: !!organizationId && !!tableName,
    queryKey: [
      QueryKey.Resource.OrganizationAdditionalColumns,
      organizationId,
      tableName,
    ],
    queryFn: async () =>
      api.organization.organizationControllerGetAdditionalColumns(
        organizationId || '',
        tableName
      ),
    ...options,
  });

export const useGetInternalPortalOrganizationList = (
  query: Parameters<
    typeof api.internalPortal.organizationsControllerGetOrganizations
  >[0],
  options?: UseQueryOptions<OrganizationListDto>
): UseQueryResult<OrganizationListDto> =>
  useQuery({
    queryKey: [
      QueryKey.Resource.InternalPortalOrganizations,
      QueryKey.Operation.List,
      query,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      api.internalPortal.organizationsControllerGetOrganizations(query),
    ...options,
  });
